import React, { useState, useEffect, useCallback } from 'react';
import {
  User,
  Building2,
  ShieldCheck,
  CreditCard,
  Bell,
  Lock,
  Code2,
  Palette,
  Globe,
  FileText,
  Camera,
  ChevronDown,
  Sun,
  Moon,
  Monitor,
  KeyRound,
  Plus,
  Plug,
  Copy,
  Trash2,
  Search,
  Check,
  UserPlus,
  Mail,
} from 'lucide-react';
import { Card, Badge, PageTitle, PrimaryButton, SecondaryButton } from './ui';
import { useAuth } from '../../context/AuthContext';
import { groupsAPI, billingAPI } from '../../services/api';
import { timeAgo } from './api';
import { LoadingRows } from './DemoCustomerAIPages';

/* ---------- sections nav ---------- */

const SECTIONS = [
  { key: 'profile', label: 'Profile', desc: 'Manage your personal information', icon: User },
  { key: 'organization', label: 'Organization & Team', desc: 'Manage your organization and members', icon: Building2 },
  { key: 'connections', label: 'Connections', desc: 'Connect tools and integrations', icon: Plug },
  { key: 'security', label: 'Security', desc: 'Manage security and SSO', icon: ShieldCheck },
  { key: 'billing', label: 'Billing', desc: 'Manage subscription and invoices', icon: CreditCard },
  { key: 'notifications', label: 'Notifications', desc: 'Manage email and in-app alerts', icon: Bell },
  { key: 'privacy', label: 'Data & Privacy', desc: 'Manage data and privacy settings', icon: Lock },
  { key: 'api', label: 'API Keys', desc: 'Manage API keys and access', icon: Code2 },
  { key: 'appearance', label: 'Appearance', desc: 'Customize your experience', icon: Palette },
  { key: 'language', label: 'Language & Region', desc: 'Set your language and region', icon: Globe },
  { key: 'audit', label: 'Audit Logs', desc: 'View system activity logs', icon: FileText },
];

/* ---------- Profile ---------- */

const Field = ({ label, value, placeholder }) => (
  <div>
    <label className="text-[13px] font-semibold">{label}</label>
    <input
      className="mt-1.5 w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-[14px] outline-none focus:border-teal-500"
      defaultValue={value}
      placeholder={placeholder}
    />
  </div>
);

const PrefRow = ({ label, desc, control }) => (
  <div className="flex items-center justify-between gap-8 border-b border-slate-50 py-4 last:border-0">
    <div>
      <p className="text-[13.5px] font-semibold">{label}</p>
      <p className="mt-0.5 text-[12.5px] text-slate-500">{desc}</p>
    </div>
    {control}
  </div>
);

const SelectControl = ({ value }) => (
  <button className="flex w-[300px] items-center justify-between rounded-xl border border-slate-200 px-3.5 py-2.5 text-[13.5px] hover:border-slate-300">
    {value} <ChevronDown size={15} className="text-slate-400" />
  </button>
);

const ProfileSection = () => {
  const [theme, setTheme] = useState('Light');
  const { user } = useAuth();
  const initials =
    (user?.name || user?.email || '?')
      .split(/[\s@]+/)
      .filter(Boolean)
      .map((p) => p[0])
      .join('')
      .slice(0, 2)
      .toUpperCase() || '?';
  return (
    <Card className="p-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-[17px] font-semibold">Profile</h2>
          <p className="mt-1 text-[13px] text-slate-500">Update your personal information and preferences.</p>
        </div>
        <SecondaryButton>
          <KeyRound size={15} /> Change Password
        </SecondaryButton>
      </div>

      <h3 className="mt-7 text-[14.5px] font-semibold">Personal Information</h3>
      <div className="mt-4 flex gap-8">
        <div className="relative shrink-0">
          <span className="flex h-[88px] w-[88px] items-center justify-center rounded-full bg-teal-600 text-[26px] font-bold text-white">
            {initials}
          </span>
          <button className="absolute -bottom-1 -right-1 flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 shadow-sm hover:bg-slate-50">
            <Camera size={14} />
          </button>
        </div>
        <div className="grid flex-1 grid-cols-1 gap-4 md:grid-cols-2">
          <Field label="Full Name" value={user?.name || ''} placeholder="Your name" />
          <Field label="Email" value={user?.email || ''} placeholder="you@example.com" />
          <Field
            label="Role"
            value={user?.userlevel >= 200 ? 'Admin' : user?.userlevel >= 100 ? 'Staff' : 'Member'}
          />
          <Field label="Phone (Optional)" value={user?.phone || ''} placeholder="+1 …" />
        </div>
      </div>

      <h3 className="mt-8 text-[14.5px] font-semibold">Preferences</h3>
      <div className="mt-2">
        <PrefRow
          label="Default Landing Page"
          desc="Choose the page you land on after login."
          control={<SelectControl value="Home" />}
        />
        <PrefRow
          label="Time Zone"
          desc="Set your local time zone."
          control={<SelectControl value="(GMT-07:00) Pacific Time (US & Canada)" />}
        />
        <PrefRow
          label="Date Format"
          desc="Choose your preferred date format."
          control={<SelectControl value="MM/DD/YYYY" />}
        />
        <PrefRow
          label="Number Format"
          desc="Choose your preferred number format."
          control={<SelectControl value="1,234.56" />}
        />
        <PrefRow
          label="Theme"
          desc="Choose your preferred theme."
          control={
            <div className="flex gap-2.5">
              {[
                { name: 'Light', icon: Sun },
                { name: 'Dark', icon: Moon },
                { name: 'System', icon: Monitor },
              ].map(({ name, icon: Icon }) => (
                <button
                  key={name}
                  onClick={() => setTheme(name)}
                  className={`flex items-center gap-2 rounded-xl border px-5 py-2.5 text-[13px] font-semibold transition-colors ${
                    theme === name
                      ? 'border-teal-500 bg-teal-50/50 text-teal-700'
                      : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  <Icon size={15} /> {name}
                </button>
              ))}
            </div>
          }
        />
      </div>

      <div className="mt-6 flex justify-end border-t border-slate-100 pt-5">
        <PrimaryButton>Save Changes</PrimaryButton>
      </div>
    </Card>
  );
};

/* ---------- Organization ---------- */

const StatTile = ({ label, value }) => (
  <div className="rounded-xl border border-slate-200 p-4">
    <p className="text-[12px] font-medium text-slate-500">{label}</p>
    <p className="mt-1 text-[20px] font-bold tracking-tight">{value}</p>
  </div>
);

const ErrorBox = ({ text }) => (
  <div className="mt-4 rounded-xl border border-red-100 bg-red-50 px-3.5 py-2.5 text-[12.5px] text-red-600">{text}</div>
);

const OrganizationSection = () => {
  const { user } = useAuth();
  const [group, setGroup] = useState(null);
  const [members, setMembers] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    Promise.all([groupsAPI.getMe(), groupsAPI.listMembers().catch(() => ({ data: {} }))])
      .then(([g, m]) => {
        setGroup(g.data || {});
        setMembers(m.data?.members || []);
      })
      .catch((e) => setError(e.response?.data?.error || e.message));
  }, []);

  const domain = user?.email ? user.email.split('@')[1] : '';
  const orgName = domain ? domain.split('.')[0].replace(/^\w/, (c) => c.toUpperCase()) : 'Your Organization';
  const memberCount = group?.memberIds?.length ?? (members ? members.length + 1 : '—');
  const role = user?.userlevel >= 200 ? 'Admin' : user?.userlevel >= 100 ? 'Staff' : 'Member';

  return (
    <Card className="p-6">
      <h2 className="text-[17px] font-semibold">Organization</h2>
      <p className="mt-1 text-[13px] text-slate-500">
        Your workspace groups scripts, agents, logs, and analytics for everyone on the team.
      </p>
      {error && <ErrorBox text={error} />}

      <div className="mt-6 flex items-center gap-4">
        <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-teal-500 to-emerald-600 text-white">
          <Building2 size={26} strokeWidth={1.7} />
        </span>
        <div>
          <p className="text-[17px] font-bold">{orgName}</p>
          <p className="text-[12.5px] text-slate-500">{domain || 'freshroad.com'}</p>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3">
        <StatTile label="Members" value={memberCount} />
        <StatTile label="Your role" value={role} />
        <StatTile label="Workspace ID" value={group?.groupId ? `…${group.groupId.slice(-6)}` : '—'} />
      </div>

      <p className="mt-5 text-[12.5px] text-slate-400">
        Manage members and invitations in the Team section below. Organization name is derived from your
        email domain and isn’t stored separately yet.
      </p>
    </Card>
  );
};

/* ---------- Team ---------- */

const TeamSection = () => {
  const { user } = useAuth();
  const isStaff = (user?.userlevel || 0) >= 100;
  const [members, setMembers] = useState(null);
  const [invitations, setInvitations] = useState([]);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviting, setInviting] = useState(false);
  const [msg, setMsg] = useState(null); // { ok, text }
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    Promise.all([groupsAPI.listMembers(), groupsAPI.listInvitations()])
      .then(([m, i]) => {
        setMembers(m.data.members || []);
        setInvitations(i.data.invitations || []);
      })
      .catch((e) => setError(e.response?.data?.error || e.message));
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const invite = async (e) => {
    e?.preventDefault?.();
    const email = inviteEmail.trim().toLowerCase();
    if (!email) return;
    setInviting(true);
    setMsg(null);
    try {
      await groupsAPI.invite(email);
      setMsg({ ok: true, text: `Invitation sent to ${email}.` });
      setInviteEmail('');
      load();
    } catch (e2) {
      setMsg({ ok: false, text: e2.response?.data?.error || e2.message });
    } finally {
      setInviting(false);
    }
  };
  const cancelInvite = async (id) => {
    try {
      await groupsAPI.cancelInvitation(id);
      load();
    } catch (e) {
      setMsg({ ok: false, text: e.response?.data?.error || e.message });
    }
  };

  const initialOf = (name, email) => ((name || email || '?').trim()[0] || '?').toUpperCase();

  return (
    <Card className="p-6">
      <h2 className="text-[17px] font-semibold">Team</h2>
      <p className="mt-1 text-[13px] text-slate-500">
        Invite people to your group. Everyone can see scripts, agents, logs, and analysis.
      </p>

      {!isStaff ? (
        <p className="mt-6 rounded-xl bg-slate-50 p-4 text-[13px] text-slate-500">
          Only staff and admins can manage the team. Ask your admin to invite members.
        </p>
      ) : (
        <>
          <form onSubmit={invite} className="mt-5 flex flex-wrap items-center gap-2.5">
            <input
              type="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              placeholder="colleague@example.com"
              className="min-w-[240px] flex-1 rounded-xl border border-slate-200 px-3.5 py-2.5 text-[14px] outline-none focus:border-teal-500"
            />
            <PrimaryButton type="submit" disabled={inviting || !inviteEmail.trim()}>
              <UserPlus size={16} /> {inviting ? 'Sending…' : 'Send invite'}
            </PrimaryButton>
          </form>
          {msg && <p className={`mt-2 text-[12.5px] ${msg.ok ? 'text-emerald-600' : 'text-red-600'}`}>{msg.text}</p>}
          {error && <ErrorBox text={error} />}

          <h3 className="mt-7 text-[14px] font-semibold">Members {members ? `(${members.length})` : ''}</h3>
          <div className="mt-3 overflow-hidden rounded-xl border border-slate-200">
            {members === null ? (
              <p className="p-4 text-[13px] text-slate-400">Loading members…</p>
            ) : members.length === 0 ? (
              <p className="p-4 text-[13px] text-slate-400">No one else in your group yet. Invite someone above.</p>
            ) : (
              <ul className="divide-y divide-slate-50">
                {members.map((m) => (
                  <li key={m.userId} className="flex items-center gap-3 px-4 py-3">
                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-teal-600 text-[13px] font-semibold text-white">
                      {initialOf(m.name, m.email)}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-[13.5px] font-semibold">{m.name || m.email}</p>
                      <p className="truncate text-[12px] text-slate-500">{m.email}</p>
                    </div>
                    <span className="text-[11.5px] text-slate-400">Joined {timeAgo(m.joinedAt)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <h3 className="mt-6 text-[14px] font-semibold">Pending invitations ({invitations.length})</h3>
          <div className="mt-3 overflow-hidden rounded-xl border border-slate-200">
            {invitations.length === 0 ? (
              <p className="p-4 text-[13px] text-slate-400">No pending invitations.</p>
            ) : (
              <ul className="divide-y divide-slate-50">
                {invitations.map((inv) => (
                  <li key={inv.id} className="flex items-center gap-3 px-4 py-3">
                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-amber-50 text-amber-500">
                      <Mail size={15} />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-[13.5px] font-semibold">{inv.email}</p>
                      <p className="text-[12px] text-slate-500">Invited · expires {timeAgo(inv.expiresAt)}</p>
                    </div>
                    <button
                      onClick={() => cancelInvite(inv.id)}
                      title="Cancel invitation"
                      className="rounded-lg p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-500"
                    >
                      <Trash2 size={15} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      )}
    </Card>
  );
};

/* ---------- Billing ---------- */

const BillingSection = () => {
  const [balance, setBalance] = useState(null);
  const [methods, setMethods] = useState([]);
  const [txns, setTxns] = useState([]);
  const [state, setState] = useState('loading'); // loading | ready | unavailable
  const [error, setError] = useState(null);

  useEffect(() => {
    billingAPI
      .getBalance()
      .then((r) => {
        setBalance(r.data);
        setState('ready');
      })
      .catch((e) => {
        if (e.response?.status === 404 || e.code === 'ERR_NETWORK') setState('unavailable');
        else {
          setError(e.response?.data?.error || e.message);
          setState('ready');
        }
      });
    billingAPI.getPaymentMethods().then((r) => setMethods(r.data.paymentMethods || [])).catch(() => {});
    billingAPI.getTransactions({ limit: 10 }).then((r) => setTxns(r.data.transactions || [])).catch(() => {});
  }, []);

  if (state === 'loading') return <LoadingRows label="Loading billing…" />;

  if (state === 'unavailable') {
    return (
      <Card className="flex min-h-[360px] flex-col items-center justify-center p-6 text-center">
        <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-teal-50 text-teal-600">
          <CreditCard size={24} strokeWidth={1.8} />
        </span>
        <h2 className="mt-4 text-[18px] font-bold">Billing</h2>
        <p className="mt-1.5 max-w-sm text-[13px] text-slate-500">
          Billing isn’t enabled on this deployment yet. It goes live with the consolidated billing server —
          balance, credits, payment methods, and transactions will appear here.
        </p>
      </Card>
    );
  }

  const fmt = balance?.balanceFormatted || (balance?.balance != null ? `$${(balance.balance / 1000).toFixed(2)}` : '$0.00');

  return (
    <div className="space-y-5">
      {error && <ErrorBox text={error} />}
      <div className="grid grid-cols-1 gap-5 xl:grid-cols-[1fr_1.3fr]">
        {/* Balance */}
        <Card className="p-6">
          <div className="flex items-start justify-between">
            <p className="text-[13.5px] font-semibold text-slate-500">Credit Balance</p>
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-teal-50 text-teal-600">
              <CreditCard size={18} />
            </span>
          </div>
          <p className="mt-2 text-[32px] font-bold tracking-tight">{fmt}</p>
          {balance?.balance != null && (
            <p className="mt-0.5 text-[12.5px] text-slate-400">{Number(balance.balance).toLocaleString()} credits</p>
          )}
          <PrimaryButton className="mt-5 w-full">
            <Plus size={16} /> Add Credits
          </PrimaryButton>
        </Card>

        {/* Payment methods */}
        <Card className="p-6">
          <h3 className="text-[15px] font-semibold">Payment Methods</h3>
          <div className="mt-4 space-y-2.5">
            {methods.length === 0 && (
              <p className="rounded-xl border border-dashed border-slate-200 p-4 text-center text-[13px] text-slate-400">
                No payment method on file.
              </p>
            )}
            {methods.map((pm) => (
              <div key={pm.id} className="flex items-center gap-3.5 rounded-xl border border-slate-200 p-3.5">
                <span className="rounded-md bg-slate-800 px-2 py-1 text-[10px] font-bold uppercase text-white">
                  {pm.brand || 'Card'}
                </span>
                <div className="flex-1">
                  <p className="text-[13.5px] font-semibold">•••• {pm.last4}</p>
                  <p className="text-[12px] text-slate-500">
                    Expires {pm.expMonth}/{pm.expYear}
                  </p>
                </div>
                {pm.isDefault && <Badge tone="teal">Default</Badge>}
              </div>
            ))}
          </div>
          <SecondaryButton className="mt-4 w-full">Manage Payment Methods</SecondaryButton>
        </Card>
      </div>

      {/* Transactions */}
      <Card className="overflow-hidden">
        <div className="px-6 pt-5">
          <h3 className="text-[15px] font-semibold">Recent Transactions</h3>
        </div>
        <table className="mt-3 w-full text-left">
          <thead>
            <tr className="border-b border-slate-100 text-[12px] text-slate-400">
              <th className="px-6 py-2.5 font-medium">Description</th>
              <th className="px-4 py-2.5 font-medium">Type</th>
              <th className="px-4 py-2.5 font-medium">Date</th>
              <th className="px-6 py-2.5 text-right font-medium">Amount</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50 text-[13px]">
            {txns.length === 0 && (
              <tr>
                <td colSpan={4} className="px-6 py-10 text-center text-[13px] text-slate-400">
                  No transactions yet.
                </td>
              </tr>
            )}
            {txns.map((t, i) => (
              <tr key={t.id || i} className="hover:bg-slate-50/60">
                <td className="px-6 py-3 font-semibold">{t.description || t.type || 'Transaction'}</td>
                <td className="px-4 py-3">
                  <Badge tone={t.type === 'top_up' || t.type === 'credit' ? 'green' : 'gray'}>{t.type || '—'}</Badge>
                </td>
                <td className="px-4 py-3 text-slate-500">{timeAgo(t.createdAt)}</td>
                <td className="px-6 py-3 text-right font-semibold">{t.amountFormatted || t.amount || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
};

/* ---------- Connections ---------- */

const CONNECTION_CATEGORIES = ['All', 'CRM', 'Communication', 'Calendar', 'Storage', 'Payments', 'Marketing', 'Healthcare', 'Automation'];

const CONNECTIONS = [
  { name: 'Salesforce', desc: 'Access CRM data, leads, and customer records.', logo: '☁️', category: 'CRM' },
  { name: 'HubSpot', desc: 'Sync contacts, deals, and marketing data.', logo: '🟠', category: 'CRM' },
  { name: 'Zoho CRM', desc: 'Manage leads and customer records.', logo: '🔴', category: 'CRM' },
  { name: 'Twilio', desc: 'Send SMS and place voice calls.', logo: '📞', category: 'Communication' },
  { name: 'Slack', desc: 'Post notifications and alerts to channels.', logo: '💬', category: 'Communication' },
  { name: 'WhatsApp Business', desc: 'Send and receive WhatsApp messages.', logo: '🟢', category: 'Communication' },
  { name: 'Microsoft Teams', desc: 'Notify teams and route conversations.', logo: '🟣', category: 'Communication' },
  { name: 'Google Calendar', desc: 'Check availability and book appointments.', logo: '📅', category: 'Calendar' },
  { name: 'Microsoft Outlook', desc: 'Sync calendar and email.', logo: '📧', category: 'Calendar' },
  { name: 'Calendly', desc: 'Automated scheduling links.', logo: '🗓️', category: 'Calendar' },
  { name: 'Google Drive', desc: 'Use documents as knowledge sources.', logo: '📁', category: 'Storage' },
  { name: 'SharePoint', desc: 'Connect enterprise document libraries.', logo: '📂', category: 'Storage' },
  { name: 'Dropbox', desc: 'Sync files and documents.', logo: '🗄️', category: 'Storage' },
  { name: 'Stripe', desc: 'Process payments and verify transactions.', logo: '💳', category: 'Payments' },
  { name: 'Square', desc: 'Payments and point-of-sale data.', logo: '⬛', category: 'Payments' },
  { name: 'Mailchimp', desc: 'Manage email campaigns and subscribers.', logo: '📨', category: 'Marketing' },
  { name: 'Epic EHR', desc: 'Retrieve patient records and appointments.', logo: '🏥', category: 'Healthcare' },
  { name: 'Cerner', desc: 'Access clinical and scheduling data.', logo: '🩺', category: 'Healthcare' },
  { name: 'Zapier', desc: 'Automate workflows across 5,000+ apps.', logo: '⚡', category: 'Automation' },
  { name: 'Webhooks', desc: 'Send events to any HTTPS endpoint.', logo: '🔗', category: 'Automation' },
];

const ConnectionsSection = () => {
  const [q, setQ] = useState('');
  const [cat, setCat] = useState('All');
  const filtered = CONNECTIONS.filter(
    (c) => (cat === 'All' || c.category === cat) && c.name.toLowerCase().includes(q.trim().toLowerCase())
  );

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-[17px] font-semibold">Connections</h2>
          <p className="mt-1 text-[13px] text-slate-500">
            Connect the tools and services your Customer AI can use to get data and take action.
          </p>
        </div>
        <Badge tone="gray">
          {CONNECTIONS.filter((c) => c.connected).length} of {CONNECTIONS.length} connected
        </Badge>
      </div>

      {/* Search + categories */}
      <div className="mt-5 flex items-center gap-2.5 rounded-xl border border-slate-200 px-3.5 py-2.5">
        <Search size={16} className="text-slate-400" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search integrations…"
          className="w-full bg-transparent text-[14px] outline-none"
        />
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        {CONNECTION_CATEGORIES.map((c) => (
          <button
            key={c}
            onClick={() => setCat(c)}
            className={`rounded-full border px-3 py-1.5 text-[12.5px] font-semibold transition-colors ${
              cat === c ? 'border-teal-500 bg-teal-50 text-teal-700' : 'border-slate-200 text-slate-500 hover:bg-slate-50'
            }`}
          >
            {c}
          </button>
        ))}
      </div>

      {/* Grid */}
      <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {filtered.map(({ name, desc, logo, category }) => (
          <div key={name} className="flex flex-col rounded-2xl border border-slate-200 p-4">
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-slate-100 bg-white text-[18px]">
                {logo}
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-[13.5px] font-semibold">{name}</p>
                <Badge tone="gray">{category}</Badge>
              </div>
            </div>
            <p className="mt-3 flex-1 text-[12.5px] leading-relaxed text-slate-500">{desc}</p>
            <button
              disabled
              title="Connections aren't available yet"
              className="mt-3 flex w-full cursor-not-allowed items-center justify-center gap-1.5 rounded-xl border border-slate-200 py-2 text-[12.5px] font-semibold text-slate-400"
            >
              <Plug size={13} /> Connect
            </button>
          </div>
        ))}
        {filtered.length === 0 && (
          <p className="col-span-full py-10 text-center text-[13px] text-slate-400">No integrations match your search.</p>
        )}
      </div>

      <p className="mt-5 text-[12px] text-slate-400">
        Integrations are coming soon — connecting a tool will let your AI read its data and take actions on your behalf.
      </p>
    </Card>
  );
};

/* ---------- API Keys ---------- */

const maskKey = (k) => `${k.slice(0, 10)}${'•'.repeat(18)}${k.slice(-4)}`;

const ApiKeysSection = () => {
  const [keys, setKeys] = useState([
    { id: 'k1', name: 'Production', key: 'fr_live_9x2Kp4mNqR7tVwYz8bClaAeFhJ', created: 'Mar 12, 2026', lastUsed: '2 hours ago', revealed: false },
    { id: 'k2', name: 'Development', key: 'fr_test_3aBc5dEf7gHi9jKl1mNo2pQr4', created: 'Jan 8, 2026', lastUsed: '5 days ago', revealed: false },
  ]);
  const [copiedId, setCopiedId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');

  const toggleReveal = (id) => setKeys((ks) => ks.map((k) => (k.id === id ? { ...k, revealed: !k.revealed } : k)));

  const copyKey = (k) => {
    try {
      navigator.clipboard?.writeText(k.key);
      setCopiedId(k.id);
      setTimeout(() => setCopiedId(null), 1500);
    } catch (_) {}
  };

  const revoke = (id) => {
    if (!window.confirm('Revoke this API key? Requests using it will stop working.')) return;
    setKeys((ks) => ks.filter((k) => k.id !== id));
  };

  const createKey = () => {
    const rand = Array.from({ length: 26 }, () => 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'[Math.floor(Math.random() * 62)]).join('');
    const key = `fr_live_${rand}`;
    setKeys((ks) => [
      { id: `k${Date.now()}`, name: newName.trim() || 'Untitled key', key, created: 'Just now', lastUsed: 'Never', revealed: true },
      ...ks,
    ]);
    setNewName('');
    setCreating(false);
  };

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-[17px] font-semibold">API Keys</h2>
          <p className="mt-1 text-[13px] text-slate-500">
            Use API keys to authenticate requests to the FreshRoad API. Keep them secret.
          </p>
        </div>
        <PrimaryButton onClick={() => setCreating((c) => !c)}>
          <Plus size={16} /> Create API Key
        </PrimaryButton>
      </div>

      {creating && (
        <div className="mt-5 flex items-end gap-3 rounded-xl border border-slate-200 bg-slate-50/60 p-4">
          <div className="flex-1">
            <label className="text-[12.5px] font-semibold text-slate-600">Key name</label>
            <input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="e.g. Production server"
              className="mt-1.5 w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-[14px] outline-none focus:border-teal-500"
            />
          </div>
          <PrimaryButton onClick={createKey}>Generate</PrimaryButton>
          <SecondaryButton onClick={() => setCreating(false)}>Cancel</SecondaryButton>
        </div>
      )}

      <div className="mt-5 overflow-hidden rounded-xl border border-slate-200">
        <table className="w-full text-left">
          <thead>
            <tr className="bg-slate-50 text-[12px] text-slate-400">
              <th className="px-4 py-2.5 font-medium">Name</th>
              <th className="px-4 py-2.5 font-medium">Key</th>
              <th className="px-4 py-2.5 font-medium">Created</th>
              <th className="px-4 py-2.5 font-medium">Last used</th>
              <th className="px-4 py-2.5 text-right font-medium">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50 text-[13px]">
            {keys.map((k) => (
              <tr key={k.id}>
                <td className="px-4 py-3 font-semibold">{k.name}</td>
                <td className="px-4 py-3">
                  <button
                    onClick={() => toggleReveal(k.id)}
                    title={k.revealed ? 'Hide' : 'Reveal'}
                    className="rounded-md bg-slate-50 px-2 py-1 font-mono text-[12px] text-slate-600 hover:bg-slate-100"
                  >
                    {k.revealed ? k.key : maskKey(k.key)}
                  </button>
                </td>
                <td className="px-4 py-3 text-slate-500">{k.created}</td>
                <td className="px-4 py-3 text-slate-500">{k.lastUsed}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1.5">
                    <button
                      onClick={() => copyKey(k)}
                      title="Copy key"
                      className="flex items-center gap-1 rounded-lg border border-slate-200 px-2.5 py-1.5 text-[12px] font-semibold hover:bg-slate-50"
                    >
                      {copiedId === k.id ? <Check size={13} className="text-emerald-500" /> : <Copy size={13} />}
                      {copiedId === k.id ? 'Copied' : 'Copy'}
                    </button>
                    <button
                      onClick={() => revoke(k.id)}
                      title="Revoke key"
                      className="rounded-lg p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-500"
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {keys.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-10 text-center text-[13px] text-slate-400">
                  No API keys. Create one to start using the API.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-4 flex items-start gap-2.5 rounded-xl bg-amber-50/70 p-3.5 text-[12.5px] text-amber-700">
        <KeyRound size={15} className="mt-0.5 shrink-0" />
        <span>
          Treat API keys like passwords. A key is shown in full only when created — store it securely.
          These keys are managed locally in the demo and aren't wired to the backend yet.
        </span>
      </div>
    </Card>
  );
};

/* ---------- placeholder ---------- */

const PlaceholderSection = ({ section }) => (
  <Card className="flex min-h-[400px] flex-col items-center justify-center p-6 text-center">
    <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-teal-50 text-teal-600">
      <section.icon size={24} strokeWidth={1.8} />
    </span>
    <h2 className="mt-4 text-[18px] font-bold">{section.label}</h2>
    <p className="mt-1.5 max-w-sm text-[13px] text-slate-500">
      {section.desc}. This section hasn't been built in the demo yet.
    </p>
  </Card>
);

/* ---------- page ---------- */

const DemoSettings = () => {
  const [active, setActive] = useState('profile');
  const section = SECTIONS.find((s) => s.key === active);

  return (
    <div className="flex h-full flex-col">
      <PageTitle title="Settings" subtitle="Manage your account, team, and platform preferences." />

      <div className="mt-6 grid min-h-0 flex-1 auto-rows-fr grid-cols-1 gap-5 overflow-hidden xl:grid-cols-[320px_1fr]">
        {/* Sections nav — scrolls internally if taller than the viewport */}
        <Card className="max-h-full overflow-y-auto p-3">
          {SECTIONS.map(({ key, label, desc, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setActive(key)}
              className={`flex w-full items-start gap-3.5 rounded-xl px-3.5 py-3 text-left transition-colors ${
                active === key ? 'bg-teal-50/70' : 'hover:bg-slate-50'
              }`}
            >
              <Icon
                size={18}
                strokeWidth={1.9}
                className={`mt-0.5 shrink-0 ${active === key ? 'text-teal-600' : 'text-slate-400'}`}
              />
              <span>
                <span className={`block text-[13.5px] font-semibold ${active === key ? 'text-teal-800' : ''}`}>
                  {label}
                </span>
                <span className="mt-0.5 block text-[12px] text-slate-500">{desc}</span>
              </span>
            </button>
          ))}
        </Card>

        {/* Content — scrolls internally */}
        <div className="min-h-0 overflow-y-auto pb-4 pr-1">
          {active === 'profile' && <ProfileSection />}
          {active === 'organization' && (
            <div className="space-y-5">
              <OrganizationSection />
              <TeamSection />
            </div>
          )}
          {active === 'billing' && <BillingSection />}
          {active === 'connections' && <ConnectionsSection />}
          {active === 'api' && <ApiKeysSection />}
          {!['profile', 'organization', 'billing', 'connections', 'api'].includes(active) && (
            <PlaceholderSection section={section} />
          )}
        </div>
      </div>
    </div>
  );
};

export default DemoSettings;
